import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { TripPlan, TripDay, TripStop } from '../data/trip-plans';
import { loadUserPlans, saveUserPlans } from '../data/trip-plans';
import { useAuth } from './AuthContext';
import {
  createMyTripPlan,
  deleteMyTripPlan,
  fetchMyTripPlans,
  patchMyTripPlan,
  type TripPlanApiDto,
} from '../lib/api';

const GUEST_PLAN_USER_ID = '__guest__';
const TRIP_MIGRATION_SS_PREFIX = 'ahg.trip_plans.migrated.';
const TRIP_LOCAL_BACKUP_KEY = 'ahg.trip_plans.v1.backup';

interface TripPlannerContextValue {
  plans: TripPlan[];
  createPlan: (
    name: string,
    daysCount: number,
    options?: {
      sortByLocation?: boolean;
      startDateISO?: string;
    },
  ) => Promise<TripPlan>;
  updatePlan: (planId: string, updates: Partial<TripPlan>) => void;
  deletePlan: (planId: string) => void;
  addPlaceToPlan: (planId: string, dayId: string, placeId: string) => void;
  removeStopFromPlan: (
    planId: string,
    dayId: string,
    stopId: string,
  ) => void;
  updateStopInPlan: (
    planId: string,
    dayId: string,
    stopId: string,
    updates: Partial<TripStop>,
  ) => void;
  reorderStops: (
    planId: string,
    dayId: string,
    startIndex: number,
    endIndex: number,
  ) => void;
}

const TripPlannerContext = createContext<TripPlannerContextValue | null>(null);

function dtoToTripPlan(d: TripPlanApiDto): TripPlan {
  const days = Array.isArray(d.days) ? (d.days as TripDay[]) : [];
  return {
    id: d.id,
    name: d.name,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    sortByLocation: d.sortByLocation,
    days,
  };
}

function tripPlanStartDateISO(plan: TripPlan): string | undefined {
  const first = plan.days?.[0]?.dateISO;
  return typeof first === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(first)
    ? first
    : undefined;
}

function planToRemoteBody(plan: TripPlan) {
  return {
    name: plan.name,
    startDate: tripPlanStartDateISO(plan),
    days: plan.days as unknown[],
    sortByLocation: plan.sortByLocation ?? false,
  };
}

export function TripPlannerProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [plans, setPlans] = useState<TripPlan[]>([]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setPlans(loadUserPlans(GUEST_PLAN_USER_ID));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let serverRows = await fetchMyTripPlans();
        let serverPlans = serverRows.map(dtoToTripPlan);
        const ssKey = `${TRIP_MIGRATION_SS_PREFIX}${user.id}`;
        if (!sessionStorage.getItem(ssKey)) {
          const localUserPlans = loadUserPlans(user.id);
          const localGuestPlans = loadUserPlans(GUEST_PLAN_USER_ID);
          const localPick =
            localUserPlans.length > 0 ? localUserPlans : localGuestPlans;

          if (serverPlans.length === 0 && localPick.length > 0) {
            for (const p of localPick) {
              await createMyTripPlan(planToRemoteBody(p));
            }
            serverRows = await fetchMyTripPlans();
            serverPlans = serverRows.map(dtoToTripPlan);
            saveUserPlans(user.id, []);
            saveUserPlans(GUEST_PLAN_USER_ID, []);
          } else if (serverPlans.length > 0 && localPick.length > 0) {
            try {
              localStorage.setItem(
                TRIP_LOCAL_BACKUP_KEY,
                JSON.stringify({
                  savedAt: new Date().toISOString(),
                  userId: user.id,
                  plans: localPick,
                }),
              );
            } catch {
              /* ignore */
            }
            saveUserPlans(user.id, []);
            saveUserPlans(GUEST_PLAN_USER_ID, []);
          }
          sessionStorage.setItem(ssKey, '1');
        }
        if (!cancelled) setPlans(serverPlans);
      } catch {
        if (!cancelled) setPlans(loadUserPlans(user.id));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, authLoading]);

  const remotePatchPlan = useCallback(
    async (planId: string, merged: TripPlan) => {
      if (!user) return;
      await patchMyTripPlan(planId, {
        name: merged.name,
        startDate: tripPlanStartDateISO(merged) ?? null,
        days: merged.days as unknown[],
        sortByLocation: merged.sortByLocation ?? false,
      });
    },
    [user],
  );

  const mapPlans = useCallback(
    (planId: string, fn: (p: TripPlan) => TripPlan) => {
      setPlans((prev) => {
        const next = prev.map((p) =>
          p.id === planId
            ? {
                ...fn(p),
                updatedAt: new Date().toISOString(),
              }
            : p,
        );
        if (!user) {
          saveUserPlans(GUEST_PLAN_USER_ID, next);
          return next;
        }
        const updated = next.find((p) => p.id === planId);
        if (updated) void remotePatchPlan(planId, updated);
        return next;
      });
    },
    [user, remotePatchPlan],
  );

  const createPlan = useCallback(
    async (
      name: string,
      daysCount: number,
      options?: {
        sortByLocation?: boolean;
        startDateISO?: string;
      },
    ): Promise<TripPlan> => {
      const addDaysISO = (iso: string, days: number): string | undefined => {
        const parts = iso.split('-').map(Number);
        if (parts.length !== 3 || parts.some(isNaN)) return undefined;
        const [y, m, d] = parts;
        const dt = new Date(y, m - 1, d);
        if (isNaN(dt.getTime())) return undefined;
        dt.setDate(dt.getDate() + days);
        const yy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${yy}-${mm}-${dd}`;
      };
      const start = options?.startDateISO;
      const newPlan: TripPlan = {
        id: `plan_${Date.now()}`,
        name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sortByLocation: options?.sortByLocation || false,
        days: Array.from({ length: daysCount }).map((_, i) => ({
          id: `day_${Date.now()}_${i}`,
          dayNumber: i + 1,
          label: `Day ${i + 1}`,
          dateISO: start ? addDaysISO(start, i) : undefined,
          timeWindow: 'full_day',
          stops: [],
        })),
      };

      if (!user) {
        setPlans((prev) => {
          const merged = [...prev, newPlan];
          saveUserPlans(GUEST_PLAN_USER_ID, merged);
          return merged;
        });
        return newPlan;
      }

      const created = await createMyTripPlan(planToRemoteBody(newPlan));
      const mapped = dtoToTripPlan(created);
      setPlans((prev) => [...prev, mapped]);
      return mapped;
    },
    [user],
  );

  const updatePlan = useCallback(
    (planId: string, updates: Partial<TripPlan>) => {
      mapPlans(planId, (p) => ({ ...p, ...updates }));
    },
    [mapPlans],
  );

  const deletePlan = useCallback(
    (planId: string) => {
      setPlans((prev) => {
        const next = prev.filter((p) => p.id !== planId);
        if (!user) {
          saveUserPlans(GUEST_PLAN_USER_ID, next);
          return next;
        }
        void deleteMyTripPlan(planId);
        return next;
      });
    },
    [user],
  );

  const addPlaceToPlan = useCallback(
    (planId: string, dayId: string, placeId: string) => {
      mapPlans(planId, (p) => ({
        ...p,
        days: p.days.map((d) => {
          if (d.id !== dayId) return d;
          const newStop: TripStop = {
            id: `stop_${Date.now()}`,
            placeId,
            startTime: '00:00',
            endTime: '00:00',
            visitDurationMinutes: 60,
            travelMode: 'drive',
            travelMinutesFromPrevious: 0,
            travelDistanceKm: 0,
          };
          return { ...d, stops: [...d.stops, newStop] };
        }),
      }));
    },
    [mapPlans],
  );

  const removeStopFromPlan = useCallback(
    (planId: string, dayId: string, stopId: string) => {
      mapPlans(planId, (p) => ({
        ...p,
        days: p.days.map((d) => {
          if (d.id !== dayId) return d;
          return { ...d, stops: d.stops.filter((s) => s.id !== stopId) };
        }),
      }));
    },
    [mapPlans],
  );

  const updateStopInPlan = useCallback(
    (
      planId: string,
      dayId: string,
      stopId: string,
      updates: Partial<TripStop>,
    ) => {
      mapPlans(planId, (p) => ({
        ...p,
        days: p.days.map((d) => {
          if (d.id !== dayId) return d;
          return {
            ...d,
            stops: d.stops.map((s) =>
              s.id === stopId ? { ...s, ...updates } : s,
            ),
          };
        }),
      }));
    },
    [mapPlans],
  );

  const reorderStops = useCallback(
    (planId: string, dayId: string, startIndex: number, endIndex: number) => {
      mapPlans(planId, (p) => ({
        ...p,
        days: p.days.map((d) => {
          if (d.id !== dayId) return d;
          const result = Array.from(d.stops);
          const [removed] = result.splice(startIndex, 1);
          result.splice(endIndex, 0, removed);
          return { ...d, stops: result };
        }),
      }));
    },
    [mapPlans],
  );

  return (
    <TripPlannerContext.Provider
      value={{
        plans,
        createPlan,
        updatePlan,
        deletePlan,
        addPlaceToPlan,
        removeStopFromPlan,
        updateStopInPlan,
        reorderStops,
      }}
    >
      {children}
    </TripPlannerContext.Provider>
  );
}

export function useTripPlanner() {
  const ctx = useContext(TripPlannerContext);
  if (!ctx)
    throw new Error('useTripPlanner must be used within TripPlannerProvider');
  return ctx;
}
